import { extractAllContentFeatures } from '@confluence/read-time/entry-points/extractAllContentFeatures.ts';

/**
 * Upper bound on nodes visited per count. The Live Docs producer recomputes on
 * every debounced edit, so the walk must stay bounded on very large documents.
 * Generous enough that typical live docs are counted in full and exactly; only a
 * document exceeding the cap yields a lower-bound count (biased to the top of the
 * document), which is acceptable — the share threshold only cares whether "enough"
 * words were added.
 */
export const MAX_WORD_COUNT_NODES = 20_000;

/**
 * Node-capped word count over an ADF document, delegating to read-time's
 * single-pass extractor so this and the read-time byline count words identically
 * (expand / nestedExpand subtrees skipped). `rawLength` is left unset so no
 * whole-document `JSON.stringify` runs on the edit path. Accepts `unknown` so the
 * editor's `JSONDocNode` can be passed without a shared type dependency.
 */
export const countWordsCapped = (doc: unknown, maxNodes: number = MAX_WORD_COUNT_NODES): number => {
	if (typeof doc !== 'object' || doc === null) {
		return 0;
	}

	const { featuresSkipExpand } = extractAllContentFeatures(
		doc as Parameters<typeof extractAllContentFeatures>[0],
		undefined,
		maxNodes,
	);
	return featuresSkipExpand.wordCount;
};
